import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const steps = [
  {
    logo: true,
    title: 'Create art together with the people you love',
    description: 'Families, friends, coworkers — anyone. Take turns, play games, and build on each other\'s art.',
  },
  {
    rossAvatar: true,
    rossLeftOfTitle: true,
    image: '/onboarding/ross-generates.png',
    title: 'Meet Ross (your AI art buddy)',
    description: 'Just describe what you want. Ross paints it for you.',
    example: 'Try: "a sunset over mountains"',
  },
  {
    image: '/onboarding/drawing-canvas.png',
    title: 'Draw Together',
    description: 'Use the built-in canvas to draw, doodle, and add your touch. Pass it to the next person and watch your art evolve.',
  },
  {
    image: '/onboarding/game-picker.png',
    title: 'Play Games',
    description: '7 family-friendly games built in: Sketch & Guess, Art Relay, Monster Mash, and more. Perfect for family game night or a quick creative break.',
  },
];

export default function OnboardingPage({ onComplete }) {
  const [step, setStep] = useState(0);

  const finish = () => {
    localStorage.setItem('app_onboarding_completed', 'true');
    if (onComplete) {
      onComplete();
    }
  };

  const isLast = step === steps.length - 1;
  const current = steps[step];

  return (
    <Container>
      <SkipButton onClick={finish}>Skip</SkipButton>

      <AnimatePresence mode="wait">
        <StepContent
          key={step}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
        >
          {current.logo ? (
            <LogoImg src="/AppLogoIcon.svg" alt="Art Ping Pong" />
          ) : current.rossLeftOfTitle ? (
            <TitleWithRoss>
              <RossImgInline src="/ross.png" alt="Ross" />
              <Title>{current.title}</Title>
            </TitleWithRoss>
          ) : null}
          
          {!current.rossLeftOfTitle && !current.logo && <Title>{current.title}</Title>}
          
          {current.image && <ScreenshotImg src={current.image} alt={current.title} />}
          {current.emoji && !current.rossLeftOfTitle && <Emoji>{current.emoji}</Emoji>}
          
          <Description>{current.description}</Description>
          {current.example && (
            <Example>{current.example}</Example>
          )}
          {current.note && (
            <Note>{current.note}</Note>
          )}
        </StepContent>
      </AnimatePresence>

      <Bottom>
        <Dots>
          {steps.map((_, i) => (
            <Dot key={i} $active={i === step} />
          ))}
        </Dots>
        <CTA onClick={isLast ? finish : () => setStep(step + 1)}>
          {isLast ? "Sign In to Get Started" : 'Next'}
        </CTA>
        <LegalFooter>
          <LegalLink href="https://app.domoreworld.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </LegalLink>
          <LegalSeparator>•</LegalSeparator>
          <LegalLink href="https://app.domoreworld.com/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </LegalLink>
        </LegalFooter>
      </Bottom>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: calc(24px + env(safe-area-inset-top, 0px)) calc(24px + env(safe-area-inset-right, 0px)) 24px calc(24px + env(safe-area-inset-left, 0px));
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 40%);
  position: relative;
`;

const SkipButton = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textTertiary};
  padding: 8px 12px;
`;

const StepContent = styled(motion.div)`
  text-align: center;
  max-width: 360px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LogoImg = styled.img`
  height: 100px;
  width: auto;
  margin-bottom: 24px;
`;

const TitleWithRoss = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  width: 100%;
  justify-content: center;
`;

const RossImgInline = styled.img`
  height: 56px;
  width: 56px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const ScreenshotImg = styled.img`
  width: 100%;
  max-width: 280px;
  height: auto;
  border-radius: ${({ theme }) => theme.radius.large};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const Emoji = styled.div`
  font-size: 72px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  
  ${TitleWithRoss} & {
    margin-bottom: 0;
    text-align: left;
    font-size: 24px;
  }
`;

const Description = styled.p`
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Example = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textTertiary};
  font-style: italic;
  margin-top: 8px;
`;

const Note = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.medium};
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding-bottom: 24px;
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.border)};
  transition: background 0.3s;
`;

const CTA = styled.button`
  width: 280px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 18px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LegalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  opacity: 0.7;
`;

const LegalLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const LegalSeparator = styled.span`
  color: ${({ theme }) => theme.colors.textTertiary};
`;
